/**
 * @file VeriomeAutocompleteField.js
 * @description autocomplete with support for adding values not 
 *              in the options list (ie., 'creatable')
 *              
 *              returns selected string, or entered string 
 *              (does not return an object)
 * 
 *              options are in the format of a string array:
 * 
 *              options =[
                    'NM_003000.2',
                    'NM_003000.3',
                    'NM_003000.4'
 *              ]
 * 
 * https://mui.com/components/autocomplete/
 * 
 * note:  the noOptionsText does not work with freeSolo
 * 
 * @copyright veriome labs, llc.  - all rights reserved.
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.md', which is part of this source code package.
 *
 * @author don michael
 * @since Mar 24, 2022 14:09
 */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import TextField from '@mui/material/TextField';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';

import { arrayExists, objectExists, objectHas } from 'src/@common/utilities';

const filter = createFilterOptions();

function VeriomeAutocompleteField(props) {
    const {
        currentValue = '',
        fieldErrors = {},
        fieldLabel,
        fieldName,
        helpText,
        isDisabled = false,
        isFocus = false,
        isFullWidth = false,
        isRequired = false,
        noOptionsText = 'No options found',
        options = [],
        sxStyle = null,
        handleBlur = () => { },
        handleChange,
    } = props;

    const [value, setValue] = useState('');
    const [localOptions, setLocalOptions] = useState([]);

    useEffect(() => {
        setValue(currentValue);
    }, [currentValue]);

    useEffect(() => {
        if (arrayExists(options)) {
            const arr = [];
            options.forEach((item) => {
                arr.push({
                    value: item,
                    label: item,
                });
            });
            setLocalOptions(arr);
        }
    }, [options]);

    const updateValue = (item) => {

        let value = '';

        // prevent null string
        if (objectExists(item) &&
            objectHas(item, 'value')) {

            value = item.value;
            setValue(item.value);

        } else {
            setValue({
                value: '',
                label: '',
            });
        }

        // set local state
        // wrap for parent change handler
        const newEvent = {
            target: {
                type: 'autocomplete',
                value: value ? value : '',
            }
        };

        // call the parent change handler
        handleChange(newEvent, fieldName);
    }

    return (
        <Autocomplete
            value={value}
            id={`${fieldName}-field`}
            disablePortal={true}
            name={`${fieldName}-field`}
            sx={sxStyle}
            fullWidth={isFullWidth}
            onBlur={() => {
                handleBlur(fieldName);
            }}
            onChange={(event, newValue) => {

                if (typeof newValue === 'string') {

                    updateValue({
                        value: newValue,
                        label: newValue,
                    });

                } else if (newValue?.inputValue) {
                    // Create a new value from the user input
                    updateValue({
                        value: newValue.inputValue,
                        label: newValue.inputValue
                    });

                } else {
                    updateValue(newValue);
                }
            }}

            filterOptions={(options, params) => {

                const filtered = filter(options, params);
                const { inputValue } = params;

                // creation of a new value
                const isExisting = options.some((option) => inputValue === option.value);

                if (inputValue !== '' && !isExisting) {
                    filtered.push({
                        inputValue,
                        label: `Add "${inputValue}"`,
                    });
                }

                return filtered;
            }}
            selectOnFocus={false}
            clearOnBlur
            handleHomeEndKeys
            options={localOptions}
            noOptionsText={noOptionsText}
            getOptionLabel={(option) => {

                // value selected with enter
                if (typeof option === 'string') {
                    return option;
                }

                // add "xxx" option created dynamically
                if (option.inputValue) {
                    return option.inputValue;
                }

                // regular option
                return option.label;
            }}
            renderOption={(props, option) => <li {...props}>{option.label}</li>}
            freeSolo
            disabled={isDisabled}
            renderInput={(params) => (
                <TextField {...params}
                    autoFocus={isFocus}
                    label={fieldLabel}
                    size="small"
                    margin="dense"
                    variant="filled"
                    required={isRequired}
                    error={fieldName ? Boolean(fieldErrors[fieldName]) : false}
                    helperText={fieldErrors[fieldName] || helpText}
                />
            )}
        />
    );
}

VeriomeAutocompleteField.propTypes = {
    currentValue: PropTypes.string,
    fieldErrors: PropTypes.object,
    fieldLabel: PropTypes.string.isRequired,
    fieldName: PropTypes.string.isRequired,
    helpText: PropTypes.string,
    isDisabled: PropTypes.bool,
    isFocus: PropTypes.bool,
    isFullWidth: PropTypes.bool,
    isRequired: PropTypes.bool,
    options: PropTypes.array,
    noOptionsText: PropTypes.string,
    sxStyle: PropTypes.object,
    handleBlur: PropTypes.func.isRequired,
    handleChange: PropTypes.func.isRequired,
};

export default VeriomeAutocompleteField;